import React from 'react';
import Button from '@cvent/carina/components/Button';
import { RedoIcon, VideoIcon } from '@components/shared/V1ToV2Icon';
import { LoadingIcon } from '@cvent/carina/components/LoadingIcon';
import { useTranslate } from 'nucleus-text';
import { SessionStatus } from '@tools/sessionUtils/SessionStatus';
import { SessionFeeRegButton } from '@components/shared/SessionFee';
import {
  ACTIVE_STREAM_STATES,
  LIVE_STATUS_VENDORS,
  TERMINAL_STREAM_STATES,
  WebcastFormat,
  Vendor,
  VendorType
} from '@tools/sessionUtils/Vendor';
import useBreakpoints from '@hooks/useBreakpoints';
import JoinSessionTooltipButton from '@components/sessionDetails/JoinSessionTooltipButton';
import useAppFeatures from '@hooks/useAppFeatures';
import useSessionInfo from '@components/sessionDetails/useSessionInfo';
import { useRouter } from 'next/router';
import useMeQuery from '@hooks/useMeQuery';
import { usePageActionsApi } from '@metrics/client/usePageActionsApi';
import useStyles from './useStyles';
import { checkIsWebcastEmbeddedCVCCollaborative } from './sessionDetailsUtils';

export type JoinSessionActionProps = {
  sessionId: string;
  // webcast vendor
  vendor?: VendorType;
  // Format of webcast (live? pre-recorded? etc)
  webcastFormat?: string;
  // Whether content is available for on-demand viewing
  onDemandVideo?: boolean;
  // Status of webcast stream
  streamStatus?: string;
  // before, during, or after session?
  sessionStatus: string;
  // Function that triggers the display of the session content for an ongoing or concluded session
  onClickEnterRoom?: () => void;
  // Whether we are actively enrolling / unenrolling
  isEnrollLoading?: boolean;
  // Whether the user has rights to session content
  isEnrolledOrIncluded?: boolean;
  // Whether content is present
  isContentPresent?: boolean;
  // External Video Link component
  externalVideoLink?: React.ReactNode;
  // Whether the vendor is embedded
  isVirtualRoomEmbedded?: boolean;
  // Is session live
  isLive?: boolean;
  // Replay button custom text
  replaySessionText?: string;
  // Shows or hides button icons. Defaults to true.
  showButtonIcon?: boolean;
  // Enables the default button container margin. Defaults to true.
  defaultButtonContainerMargin?: boolean;
  // Shows the disabled join session button .
  disableJoinButton?: boolean;
  sessionName?: string;
  /**
   * Flag whether to allow moderator, host, and/or speaker to access and dry run a Pre-Recorded session/presentation in Webinar Event as an On-Demand Video before the session start time until 5 minutes before the session start time.
   * At 5 minutes before session start time, the Pre-Recorded Presentation will start playing as Simulive.
   */
  allowDryRunPreRecordedSession?: boolean;
  // Is the attendee a moderator/speaker/host
  hasHigherPermission?: boolean;
  isHostOrModeratorOrSpeaker?: boolean;
  // For testing negative states, pass one of these in unit tests so the loading state is not null
  loadingElement?: React.ReactNode;
  sessionHasFee?: boolean;
  isRegModEnabled?: boolean;
  // If the virtual room is already present, do not show any join actions
  showVirtualRoom?: boolean;
  // To set the style of the button to filled or lined based on the order of this button in the list of buttons to join the session
  appearance?: 'filled' | 'lined';
};

function JoinSessionAction({
  sessionId,
  vendor,
  webcastFormat,
  onDemandVideo,
  streamStatus,
  sessionStatus,
  onClickEnterRoom,
  isEnrollLoading,
  isEnrolledOrIncluded,
  isContentPresent,
  externalVideoLink,
  isVirtualRoomEmbedded,
  isLive,
  replaySessionText,
  showButtonIcon = true,
  defaultButtonContainerMargin = true,
  disableJoinButton = false,
  sessionName,
  allowDryRunPreRecordedSession = false,
  hasHigherPermission = false,
  isHostOrModeratorOrSpeaker = false,
  loadingElement = null,
  sessionHasFee = false,
  isRegModEnabled = false,
  showVirtualRoom = false,
  appearance = 'filled'
}: JoinSessionActionProps): JSX.Element {
  const { translate } = useTranslate();
  const { sessionDetailActionButtonContainer, sessionDetailActionButtonContainerNoMargin } = useStyles();
  const { isMobile } = useBreakpoints();
  const { mockSessionVideoConfiguration, cventVideoVersionOneUnavailable } = useAppFeatures();
  const { sessionDetailsResponse } = useSessionInfo({
    sessionId
  });
  const sessionDetails = sessionDetailsResponse?.data?.sessionDetail;
  const sessionDetailsLoading = sessionDetailsResponse?.loading;
  const showCventVideoOne = !cventVideoVersionOneUnavailable || vendor !== Vendor.CVP;
  const router = useRouter();
  const { data: meData, loading: meLoading } = useMeQuery();
  const { openOnDemandAsset } = usePageActionsApi();

  const buttonContainerStyle = defaultButtonContainerMargin
    ? sessionDetailActionButtonContainer
    : sessionDetailActionButtonContainerNoMargin;

  const hasFee = sessionHasFee && !isMobile && !isHostOrModeratorOrSpeaker;

  if (isEnrolledOrIncluded && !isContentPresent && !isHostOrModeratorOrSpeaker) {
    return null;
  }

  const purchaseRegistrationButton = (
    <div css={buttonContainerStyle}>
      <SessionFeeRegButton isRegModEnabled={isRegModEnabled} onClick={onClickEnterRoom} />
    </div>
  );

  if (!isVirtualRoomEmbedded && externalVideoLink) {
    if (hasFee && !hasHigherPermission) {
      return purchaseRegistrationButton;
    }

    return <>{externalVideoLink}</>;
  }

  const loadingOrVideoIcon = isEnrollLoading ? LoadingIcon : VideoIcon;
  const loadingOrRedoIcon = isEnrollLoading ? LoadingIcon : RedoIcon;

  // TODO:Embedded Cvent Video Conferencing if we want to include cvent video in vendors providing live status
  // if new fields are enabled and stream status value is populated, use live stream status to determine which overlay to show

  if (meLoading || sessionDetailsLoading) {
    return <>{loadingElement}</>;
  }

  if (LIVE_STATUS_VENDORS.has(vendor) && webcastFormat === WebcastFormat.LIVE && streamStatus && !showVirtualRoom) {
    if (hasFee) {
      return purchaseRegistrationButton;
    }

    if (
      streamStatus === 'Standby' &&
      (sessionStatus === SessionStatus.LESS_THAN_FIVE_MIN_BEFORE || sessionStatus === SessionStatus.DURING)
    ) {
      return (
        <div css={buttonContainerStyle}>
          <Button
            appearance={appearance}
            size={isMobile ? 's' : 'l'}
            testID="enter-session-button"
            text={
              hasHigherPermission
                ? translate('Virtual-Room-Overlay-Enter-Session-Button-HighPermission')
                : translate('Virtual-Room-Overlay-Enter-Session-Button')
            }
            icon={showButtonIcon ? loadingOrVideoIcon : null}
            onClick={onClickEnterRoom}
          />
        </div>
      );
    }
    if (
      (ACTIVE_STREAM_STATES.has(streamStatus) &&
        (sessionStatus === SessionStatus.LESS_THAN_FIVE_MIN_BEFORE || sessionStatus === SessionStatus.DURING)) ||
      (streamStatus === 'Processing' && sessionStatus === SessionStatus.AFTER)
    ) {
      return (
        <div css={buttonContainerStyle}>
          <Button
            appearance={appearance}
            size={isMobile ? 's' : 'l'}
            testID="enter-session-button"
            text={
              hasHigherPermission
                ? translate('Virtual-Room-Overlay-Enter-Session-Button-HighPermission')
                : translate('Virtual-Room-Overlay-Enter-Session-Button')
            }
            icon={showButtonIcon ? loadingOrVideoIcon : null}
            onClick={onClickEnterRoom}
          />
        </div>
      );
    }
    if (
      (TERMINAL_STREAM_STATES.has(streamStatus) &&
        (sessionStatus === SessionStatus.DURING || sessionStatus === SessionStatus.AFTER)) ||
      (streamStatus === 'Standby' && sessionStatus === SessionStatus.AFTER)
    ) {
      return onDemandVideo && showCventVideoOne ? (
        <div css={buttonContainerStyle}>
          <Button
            appearance={appearance}
            size={isMobile ? 's' : 'l'}
            testID="replay-session-button"
            text={replaySessionText || translate('Virtual-Room-Overlay-After-Session-WatchRecording-Button')}
            icon={showButtonIcon ? loadingOrRedoIcon : null}
            onClick={() => {
              onClickEnterRoom();
              openOnDemandAsset({
                videoType: 'On Demand Session'
              });
            }}
          />
        </div>
      ) : null;
    }
    if (
      (sessionStatus === SessionStatus.MORE_THAN_FIVE_MIN_BEFORE ||
        sessionStatus === SessionStatus.MORE_THAN_TEN_MIN_BEFORE ||
        sessionStatus === SessionStatus.MORE_THAN_ONE_HOUR_BEFORE) &&
      isHostOrModeratorOrSpeaker
    ) {
      return (
        <div css={buttonContainerStyle}>
          <Button
            appearance={appearance}
            size={isMobile ? 's' : 'l'}
            testID="enter-session-button"
            text={translate('Virtual-Room-Overlay-Enter-Session-Button')}
            icon={showButtonIcon ? loadingOrVideoIcon : null}
            onClick={onClickEnterRoom}
          />
        </div>
      );
    }
    return disableJoinButton ? (
      <div css={buttonContainerStyle}>
        <JoinSessionTooltipButton
          size={isMobile ? 's' : 'l'}
          sessionName={sessionName}
          hasHigherPermission={hasHigherPermission}
        />
      </div>
    ) : null;
  }

  if (isVirtualRoomEmbedded && !showVirtualRoom) {
    if (hasFee) {
      return purchaseRegistrationButton;
    }

    // Default to session time
    switch (sessionStatus) {
      case SessionStatus.LESS_THAN_FIVE_MIN_BEFORE:
      case SessionStatus.DURING:
        // ADR - Session Details Refactor
        // This is temporary logic, just an example of gating the particular video configuration by feature flag so
        // liveCVCCollaborative use the new virtual room page as an example
        if (
          checkIsWebcastEmbeddedCVCCollaborative(
            sessionDetails?.webcast?.format,
            sessionDetails?.webcast?.type,
            sessionDetails?.webcast?.solutionType
          ) &&
          mockSessionVideoConfiguration
        ) {
          return (
            <div css={buttonContainerStyle}>
              <Button
                appearance={appearance}
                size={isMobile ? 's' : 'l'}
                testID="enter-session-button"
                text={
                  hasHigherPermission
                    ? translate('Virtual-Room-Overlay-Enter-Session-Button-HighPermission')
                    : translate('Virtual-Room-Overlay-Enter-Session-Button')
                }
                // TODO: Embedded Cvent Video Conferencing icon to be decided
                icon={showButtonIcon ? loadingOrVideoIcon : null}
                onClick={() => {
                  router.push(`/events/${meData?.me?.event?.id}/sessions/${sessionDetails?.id}/virtual-room`);
                }}
                autoFocus
              />
            </div>
          );
        }
        return (
          <div css={buttonContainerStyle}>
            <Button
              appearance={appearance}
              size={isMobile ? 's' : 'l'}
              testID="enter-session-button"
              text={
                hasHigherPermission
                  ? translate('Virtual-Room-Overlay-Enter-Session-Button-HighPermission')
                  : translate('Virtual-Room-Overlay-Enter-Session-Button')
              }
              // todo:Embedded Cvent Video Conferencing icon to be decided
              icon={showButtonIcon ? loadingOrVideoIcon : null}
              onClick={onClickEnterRoom}
              autoFocus
            />
          </div>
        );

      case SessionStatus.AFTER:
        if (isLive) {
          return (
            <div css={buttonContainerStyle}>
              <Button
                appearance={appearance}
                size={isMobile ? 's' : 'l'}
                testID="enter-session-button"
                text={
                  hasHigherPermission
                    ? translate('Virtual-Room-Overlay-Enter-Session-Button-HighPermission')
                    : translate('Virtual-Room-Overlay-Enter-Session-Button')
                }
                icon={showButtonIcon ? loadingOrVideoIcon : null}
                onClick={onClickEnterRoom}
              />
            </div>
          );
        }
        return onDemandVideo && showCventVideoOne ? (
          <div css={buttonContainerStyle}>
            <Button
              appearance={appearance}
              size={isMobile ? 's' : 'l'}
              testID="replay-session-button"
              text={replaySessionText || translate('Virtual-Room-Overlay-After-Session-WatchRecording-Button')}
              icon={showButtonIcon ? loadingOrRedoIcon : null}
              onClick={() => {
                onClickEnterRoom();
                openOnDemandAsset({
                  videoType: 'On Demand Session'
                });
              }}
            />
          </div>
        ) : null;

      case SessionStatus.MORE_THAN_FIVE_MIN_BEFORE:
      case SessionStatus.MORE_THAN_ONE_HOUR_BEFORE:
        if (disableJoinButton) {
          return (
            <div css={buttonContainerStyle}>
              <JoinSessionTooltipButton
                size={isMobile ? 's' : 'l'}
                sessionName={sessionName}
                hasHigherPermission={hasHigherPermission}
              />
            </div>
          );
        }
        if (allowDryRunPreRecordedSession) {
          return (
            <div css={buttonContainerStyle}>
              <Button
                appearance={appearance}
                size={isMobile ? 's' : 'l'}
                text={
                  hasHigherPermission
                    ? translate('Virtual-Room-Overlay-Enter-Session-Button-HighPermission')
                    : translate('Virtual-Room-Overlay-Enter-Session-Button')
                }
                icon={showButtonIcon ? loadingOrVideoIcon : null}
                onClick={onClickEnterRoom}
                testID="join-dryrun-prerecorded-session-button"
              />
            </div>
          );
        }
        return null;
      default:
        return null;
    }
  }
  return null;
}

export default JoinSessionAction;
